import type {
  Car,
  FactoryMatch,
  Step,
  StepConfiguration,
  Substep,
} from "../Upholstery/types"
import { fetchPdmProductsRecords } from "@views/Upholstery/pdm/pdmStepsClient"
import { filterFactoryMatchesForActiveConfig } from "@views/UpholsteryV2/helpers/factory/factory-match-filter"
import {
  isRowCountAwareRowsSubstepVisible,
  resolveRowCountFromRowsConfig,
  ROW_COUNT_SUBSTEP_KEY,
  ROW_COUNT_THREE,
  ROW_COUNT_TWO,
  shouldShowMiddleRow,
  vehicleNeedsRowCountChoice,
} from "@views/UpholsteryV2/constants/row-count-choice"
import { filterOptionsBySelectedTrim } from "@views/UpholsteryV2/helpers/steps/trimCompatibleOptions"
import { isPdmStructureV2Enabled } from "@views/UpholsteryV2/helpers/flags/isPdmStructureV2Enabled"
import { buildPdmStockingStatusMap } from "@views/UpholsteryV2/pdm/normalizePdm"
import {
  DropdownOption,
  getStockingStatusLabel,
  InventoryResult,
  isInStockStatus,
  lookupStockingStatus,
  mapFactoryMatchOptions,
  StockFilter,
} from "./types"

/** Same slug rules as navbar `useSaveVehicleToStore`. */
export function toVehicleSlug(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
}

/**
 * Map URL/store make+model (display name or slug) onto picker catalog values.
 * Year is set only when the matched car lists that year.
 */
export function resolveYmmFromPickerCars(params: {
  cars: Car[]
  make: string
  model: string
  year?: string
}): { make: string; model: string; year: string } | null {
  const makeInput = params.make.trim()
  const modelInput = params.model.trim()
  const yearInput = (params.year || "").trim()

  if (!makeInput || !modelInput || params.cars.length === 0) {
    return null
  }

  const makeSlug = toVehicleSlug(makeInput)
  const modelSlug = toVehicleSlug(modelInput)

  const car = params.cars.find((item) => {
    if (item.make === makeInput && item.model === modelInput) return true
    return (
      toVehicleSlug(item.make) === makeSlug &&
      toVehicleSlug(item.model) === modelSlug
    )
  })

  if (!car) return null

  const years = car.years
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean)

  return {
    make: car.make,
    model: car.model,
    year: yearInput && years.includes(yearInput) ? yearInput : "",
  }
}

/** Landing URL after Shop by Vehicle BUILD (modern). */
export function buildUpholsteryLandingHref(
  make: string,
  model: string,
  year: string
): string {
  const params = new URLSearchParams({ make, model, year })
  return `/upholstery?${params.toString()}`
}

/**
 * Factory-match kit price from the vehicle build step.
 * 3-row kits use threeRowPrice. Everything else uses twoRowPrice.
 * Returns null when the factory option has no price.
 */
export function getFactoryBuildPrice(
  steps: Step[] | undefined,
  rowCount: 2 | 3
): number | null {
  const build = getStepByKey(steps, "build")
  const factory = build?.category?.optionValues?.find(
    (option) => option.optionValue.trim().toLowerCase() === "factory"
  )
  if (!factory) return null
  const raw = rowCount === 3 ? factory.threeRowPrice : factory.twoRowPrice
  if (raw == null) return null
  const price = Number(raw)
  return Number.isFinite(price) ? price : null
}

/**
 * 2 or 3 rows for the factory-match price, using the configurator's rule.
 * Vehicles with How Many Rows use that answer.
 * Vehicles without it count as 3-row when they have middle row options,
 * even before a middle row is picked.
 */
export function getInventoryRowCount(
  steps: Step[] | undefined,
  rowCount: string,
  middleRow: string
): 2 | 3 {
  const substeps: NonNullable<StepConfiguration[string]["substeps"]> = {}
  if (vehicleNeedsRowCountChoice(steps ? { steps } : null)) {
    substeps[ROW_COUNT_SUBSTEP_KEY] = { value: rowCount, label: rowCount }
  }
  const hasMiddle =
    Boolean(middleRow) || getSubstepOptions(steps, "rows", "middle").length > 0
  if (hasMiddle) {
    substeps.middle = { value: middleRow, label: middleRow }
  }
  return resolveRowCountFromRowsConfig({
    rows: { selectedValue: "", substeps },
  })
}

export function getStepByKey(steps: Step[] | undefined, key: string) {
  return steps?.find((step) => step.key === key)
}

export function getSubstepOptions(
  steps: Step[] | undefined,
  stepKey: string,
  substepKey: string
): DropdownOption[] {
  const step = getStepByKey(steps, stepKey)
  const substep = step?.substeps?.find((item) => item.key === substepKey)
  return (
    substep?.category?.optionValues?.map((option) => ({
      label: option.optionName || option.optionValue,
      value: option.optionValue,
      image: option.optionImage?.responsiveImage?.src || "/bucket.png",
      sku: option.sku,
      slug: option.slug,
      compatibility: option.compatibility,
    })) || []
  )
}

/**
 * Same lists the custom builder limits after a trim is chosen:
 * body type, both seat types, middle row, and back row.
 * Front row is not limited by trim there, so it stays open here too.
 */
export function isTrimFilteredSubstep(stepKey: string, substepKey: string) {
  return (
    (stepKey === "body" &&
      (substepKey === "body-step" ||
        substepKey === "driver-seat-type" ||
        substepKey === "passenger-seat-type")) ||
    (stepKey === "rows" && (substepKey === "middle" || substepKey === "back"))
  )
}

export function getTrimFilteredSubstepOptions(
  steps: Step[] | undefined,
  stepKey: string,
  substepKey: string,
  selectedTrim: string
): DropdownOption[] {
  const options = getSubstepOptions(steps, stepKey, substepKey)
  if (!isTrimFilteredSubstep(stepKey, substepKey)) return options
  return filterOptionsBySelectedTrim(options, selectedTrim)
}

/** Keep the current choice when it is still allowed. Otherwise use the first allowed choice. */
export function keepOrFirstOption(
  current: string,
  options: DropdownOption[]
): string {
  if (current && options.some((option) => option.value === current)) {
    return current
  }
  return options[0]?.value || ""
}

export interface InventorySelections {
  trim: string
  body: string
  driver: string
  passenger: string
  front: string
  middle: string
  back: string
}

/**
 * Used when Search runs, or when a link opens the page.
 * An empty current value falls through to the first allowed choice.
 */
export function resolveInventorySelections(
  steps: Step[] | undefined,
  current: Partial<InventorySelections>
): InventorySelections {
  const trim = keepOrFirstOption(
    current.trim || "",
    getSubstepOptions(steps, "body", "trim")
  )

  return {
    trim,
    body: keepOrFirstOption(
      current.body || "",
      getTrimFilteredSubstepOptions(steps, "body", "body-step", trim)
    ),
    driver: keepOrFirstOption(
      current.driver || "",
      getTrimFilteredSubstepOptions(steps, "body", "driver-seat-type", trim)
    ),
    passenger: keepOrFirstOption(
      current.passenger || "",
      getTrimFilteredSubstepOptions(steps, "body", "passenger-seat-type", trim)
    ),
    front: keepOrFirstOption(
      current.front || "",
      getSubstepOptions(steps, "rows", "front")
    ),
    middle: keepOrFirstOption(
      current.middle || "",
      getTrimFilteredSubstepOptions(steps, "rows", "middle", trim)
    ),
    back: keepOrFirstOption(
      current.back || "",
      getTrimFilteredSubstepOptions(steps, "rows", "back", trim)
    ),
  }
}

/**
 * Body and Back stay empty until the shopper picks one.
 * If they already picked one and the new trim does not allow it, use the first choice that is still allowed.
 */
function replaceInvalidBodyOrBack(
  current: string,
  options: DropdownOption[]
): string {
  if (!current) return ""
  if (options.some((option) => option.value === current)) return current
  return options[0]?.value || ""
}

/**
 * Middle stays empty until the shopper picks one.
 * If the old pick is no longer allowed and only one choice remains, use that one.
 * If several choices remain, clear the field.
 */
function replaceInvalidMiddle(
  current: string,
  options: DropdownOption[]
): string {
  if (!current) return ""
  if (options.some((option) => option.value === current)) return current
  if (options.length === 1) return options[0].value
  return ""
}

/**
 * Same reset the custom builder uses after the trim changes.
 * A blank trim does not rewrite the other fields.
 * Front row is not filled in from the trim.
 * Driver and passenger seats still fall through to the first allowed choice.
 */
export function alignInventorySelectionsAfterTrimChange(
  steps: Step[] | undefined,
  current: Partial<InventorySelections>
): InventorySelections {
  const trim = current.trim || ""
  const unchanged: InventorySelections = {
    trim,
    body: current.body || "",
    driver: current.driver || "",
    passenger: current.passenger || "",
    front: current.front || "",
    middle: current.middle || "",
    back: current.back || "",
  }
  if (!trim.trim()) return unchanged

  return {
    trim,
    body: replaceInvalidBodyOrBack(
      current.body || "",
      getTrimFilteredSubstepOptions(steps, "body", "body-step", trim)
    ),
    driver: keepOrFirstOption(
      current.driver || "",
      getTrimFilteredSubstepOptions(steps, "body", "driver-seat-type", trim)
    ),
    passenger: keepOrFirstOption(
      current.passenger || "",
      getTrimFilteredSubstepOptions(steps, "body", "passenger-seat-type", trim)
    ),
    front: current.front || "",
    middle: replaceInvalidMiddle(
      current.middle || "",
      getTrimFilteredSubstepOptions(steps, "rows", "middle", trim)
    ),
    back: replaceInvalidBodyOrBack(
      current.back || "",
      getTrimFilteredSubstepOptions(steps, "rows", "back", trim)
    ),
  }
}

/**
 * Same show and hide rules as the custom builder rows step.
 * How Many Rows appears only when the vehicle has that step.
 * Front, Middle, and Back stay hidden until 2 Row or 3 Row is chosen.
 * Middle stays hidden on 2 Row, and when the vehicle has no middle options.
 */
export function inventoryRowsVisibility(
  steps: Step[] | undefined,
  rowCount: string
): {
  showRowCount: boolean
  showFront: boolean
  showMiddle: boolean
  showBack: boolean
} {
  const needsRowCountChoice = vehicleNeedsRowCountChoice(
    steps ? { steps } : null
  )
  const confirmed = rowCount === ROW_COUNT_TWO || rowCount === ROW_COUNT_THREE
  const showMiddle =
    shouldShowMiddleRow(
      needsRowCountChoice
        ? {
            rows: {
              selectedValue: rowCount,
              substeps: {
                [ROW_COUNT_SUBSTEP_KEY]: {
                  value: rowCount,
                  label: rowCount,
                },
              },
            },
          }
        : undefined
    ) && getSubstepOptions(steps, "rows", "middle").length > 0

  const opts = { needsRowCountChoice, confirmed, showMiddle }
  return {
    showRowCount: needsRowCountChoice,
    showFront: isRowCountAwareRowsSubstepVisible("front", opts),
    showMiddle: isRowCountAwareRowsSubstepVisible("middle", opts),
    showBack: isRowCountAwareRowsSubstepVisible("back", opts),
  }
}

/** Drop a row value when that dropdown is hidden, so search does not use it. */
export function applyVisibleRowSelections(
  steps: Step[] | undefined,
  rowCount: string,
  selections: InventorySelections
): InventorySelections {
  const visibility = inventoryRowsVisibility(steps, rowCount)
  return {
    ...selections,
    front: visibility.showFront ? selections.front : "",
    middle: visibility.showMiddle ? selections.middle : "",
    back: visibility.showBack ? selections.back : "",
  }
}

export function getSubstep(
  steps: Step[] | undefined,
  stepKey: string,
  substepKey: string
): Substep | undefined {
  return getStepByKey(steps, stepKey)?.substeps?.find(
    (item) => item.key === substepKey
  )
}

export function getFactoryMatches(steps: Step[] | undefined): FactoryMatch[] {
  const factoryStep = getStepByKey(steps, "factory-match")
  if (factoryStep?.factoryMatches?.length) {
    return factoryStep.factoryMatches
  }

  const nested = factoryStep?.substeps?.flatMap(
    (substep) => substep.factoryMatches || []
  )
  return nested || []
}

/** PDM `Inventory` when V2 is on; otherwise Dato `stockingStatus`. */
export async function resolveInventoryStockingStatusMap(params: {
  make: string
  model: string
  year: string
  datoStockingStatus?: Record<string, string>
}): Promise<Record<string, string> | undefined> {
  const { make, model, year, datoStockingStatus } = params

  const v2On = isPdmStructureV2Enabled()

  if (!v2On) {
    return datoStockingStatus
  }

  const products = (await fetchPdmProductsRecords()) ?? []
  if (!products.length) {
    return datoStockingStatus
  }

  return buildPdmStockingStatusMap(products, make, model, year)
}

function findOptionLabel(
  options: DropdownOption[],
  value: string
): DropdownOption | undefined {
  return options.find((option) => option.value === value)
}

function substepSelection(option: DropdownOption | undefined) {
  if (!option) return undefined
  return {
    value: option.value,
    label: option.label,
    slug: option.slug,
    sku: option.sku,
  }
}

/** Map inventory dropdown values into `StepConfiguration` for V2 PDM filtering. */
export function buildInventoryStepConfiguration(params: {
  steps: Step[] | undefined
  trimValue: string
  bodyValue: string
  driverValue: string
  passengerValue: string
  frontValue: string
  backValue: string
  middleValue?: string
}): StepConfiguration {
  const {
    steps,
    trimValue,
    bodyValue,
    driverValue,
    passengerValue,
    frontValue,
    backValue,
    middleValue = "",
  } = params

  const trimOpt = findOptionLabel(
    getSubstepOptions(steps, "body", "trim"),
    trimValue
  )
  const bodyOpt = findOptionLabel(
    getSubstepOptions(steps, "body", "body-step"),
    bodyValue
  )
  const driverOpt = findOptionLabel(
    getSubstepOptions(steps, "body", "driver-seat-type"),
    driverValue
  )
  const passengerOpt = findOptionLabel(
    getSubstepOptions(steps, "body", "passenger-seat-type"),
    passengerValue
  )
  const frontOpt = findOptionLabel(
    getSubstepOptions(steps, "rows", "front"),
    frontValue
  )
  const backOpt = findOptionLabel(
    getSubstepOptions(steps, "rows", "back"),
    backValue
  )
  const middleOpt = findOptionLabel(
    getTrimFilteredSubstepOptions(steps, "rows", "middle", trimValue),
    middleValue
  )

  const bodySubsteps: NonNullable<StepConfiguration["body"]>["substeps"] = {}
  const trimSel = substepSelection(trimOpt)
  const bodySel = substepSelection(bodyOpt)
  const driverSel = substepSelection(driverOpt)
  const passengerSel = substepSelection(passengerOpt)
  if (trimSel) bodySubsteps.trim = trimSel
  if (bodySel) bodySubsteps["body-step"] = bodySel
  if (driverSel) bodySubsteps["driver-seat-type"] = driverSel
  if (passengerSel) bodySubsteps["passenger-seat-type"] = passengerSel

  const rowSubsteps: NonNullable<StepConfiguration["rows"]>["substeps"] = {}
  const frontSel = substepSelection(frontOpt)
  const backSel = substepSelection(backOpt)
  const middleSel = substepSelection(middleOpt)
  if (frontSel) rowSubsteps.front = frontSel
  if (middleSel) rowSubsteps.middle = middleSel
  if (backSel) rowSubsteps.back = backSel

  const config: StepConfiguration = {}

  if (Object.keys(bodySubsteps).length > 0) {
    config.body = {
      selectedValue: trimValue || bodyValue || driverValue || passengerValue,
      substeps: bodySubsteps,
    }
  }

  if (Object.keys(rowSubsteps).length > 0) {
    config.rows = {
      selectedValue: frontValue || middleValue || backValue,
      substeps: rowSubsteps,
    }
  }

  return config
}

export function buildInventoryResults(params: {
  factoryMatches: FactoryMatch[]
  stockingStatus: Record<string, string> | undefined
  make: string
  model: string
  year: string
  trim: string
  bodyType: string
  driverSeatType: string
  passengerSeatType: string
  frontRow: DropdownOption | undefined
  middleRow?: DropdownOption | undefined
  backRow: DropdownOption | undefined
}): InventoryResult[] {
  const {
    factoryMatches,
    stockingStatus,
    make,
    model,
    year,
    trim,
    bodyType,
    driverSeatType,
    passengerSeatType,
    frontRow,
    middleRow,
    backRow,
  } = params

  return factoryMatches.map((match) => {
    const sku = match.sku || ""
    const rawStatus = lookupStockingStatus(stockingStatus, sku)
    const isInStock = isInStockStatus(rawStatus)

    return {
      id: match.id,
      sku,
      status: getStockingStatusLabel(rawStatus),
      isInStock,
      rawStockingStatus: rawStatus || "",
      make,
      model,
      year,
      vehicleLabel: `${year} ${make} ${model}`.trim(),
      trim,
      bodyType,
      driverSeatType,
      passengerSeatType,
      frontRowLabel: frontRow?.label || "—",
      frontRowImage: frontRow?.image || "",
      middleRowLabel: middleRow?.label || "—",
      middleRowImage: middleRow?.image || "",
      backRowLabel: backRow?.label || "—",
      backRowImage: backRow?.image || "",
      options: mapFactoryMatchOptions(match.factoryMatchOption),
    }
  })
}

/**
 * Filter Factory Match cards with the same V2 rules as the configurator, then
 * build inventory result rows (stocking from PDM `Inventory` when V2 is on, else Dato).
 */
export function buildFilteredInventoryResults(params: {
  steps: Step[] | undefined
  vehicleOptions: unknown
  stockingStatus: Record<string, string> | undefined
  make: string
  model: string
  year: string
  trimValue: string
  bodyValue: string
  driverValue: string
  passengerValue: string
  frontValue: string
  backValue: string
  middleValue?: string
}): InventoryResult[] {
  const {
    steps,
    vehicleOptions,
    stockingStatus,
    make,
    model,
    year,
    trimValue,
    bodyValue,
    driverValue,
    passengerValue,
    frontValue,
    backValue,
    middleValue = "",
  } = params

  const allMatches = getFactoryMatches(steps)
  const activeConfig = buildInventoryStepConfiguration({
    steps,
    trimValue,
    bodyValue,
    driverValue,
    passengerValue,
    frontValue,
    backValue,
    middleValue,
  })
  const filteredMatches = filterFactoryMatchesForActiveConfig(
    allMatches,
    activeConfig,
    steps,
    vehicleOptions
  )

  const frontOption = findOptionLabel(
    getSubstepOptions(steps, "rows", "front"),
    frontValue
  )
  const middleOption = findOptionLabel(
    getSubstepOptions(steps, "rows", "middle"),
    middleValue
  )
  const backOption = findOptionLabel(
    getSubstepOptions(steps, "rows", "back"),
    backValue
  )

  return buildInventoryResults({
    factoryMatches: filteredMatches,
    stockingStatus,
    make,
    model,
    year,
    trim:
      findOptionLabel(getSubstepOptions(steps, "body", "trim"), trimValue)
        ?.label || trimValue,
    bodyType:
      findOptionLabel(getSubstepOptions(steps, "body", "body-step"), bodyValue)
        ?.label || bodyValue,
    driverSeatType:
      findOptionLabel(
        getSubstepOptions(steps, "body", "driver-seat-type"),
        driverValue
      )?.label || driverValue,
    passengerSeatType:
      findOptionLabel(
        getSubstepOptions(steps, "body", "passenger-seat-type"),
        passengerValue
      )?.label || passengerValue,
    frontRow: frontOption,
    middleRow: middleOption,
    backRow: backOption,
  })
}

export function parseStockFilter(value: unknown): StockFilter {
  return value === "special-order" ? "special-order" : "in-stock"
}

/**
 * Filter by landing path, then always put in-stock kits first.
 *
 * - in-stock path, checkbox off → in-stock only ("Show out of stock")
 * - special-order path, checkbox off → special-order only ("Show in stock")
 * - checkbox on → both groups, in-stock first
 */
export function filterResultsByStock(
  results: InventoryResult[],
  stockFilter: StockFilter,
  showOtherGroup: boolean
): InventoryResult[] {
  const preferInStock = stockFilter === "in-stock"

  let filtered = results
  if (!showOtherGroup) {
    filtered = results.filter((result) =>
      preferInStock ? result.isInStock : !result.isInStock
    )
  }

  // Always list in-stock kits before special-order kits.
  return sortInventoryResultsByStock(filtered, true)
}

export function sortInventoryResultsByStock(
  results: InventoryResult[],
  preferInStock: boolean
): InventoryResult[] {
  return [...results].sort((a, b) => {
    if (a.isInStock === b.isInStock) return 0
    if (preferInStock) {
      return a.isInStock ? -1 : 1
    }
    return a.isInStock ? 1 : -1
  })
}

export { findOptionLabel }
